import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

// Format a numeric value for display using comma as decimal separator
const formatNumericForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (isNaN(num)) return '';
  return String(num).replace('.', ',');
};

const NumericCellEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(formatNumericForDisplay(props.value));

  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (value === '' || value === null || value === undefined) return null;
      // Replace comma with dot for parseFloat
      const normalized = String(value).replace(',', '.');
      const numValue = parseFloat(normalized);
      return isNaN(numValue) ? null : numValue;
    },
    isCancelAfterEnd: () => {
      // Allow empty values for deletion
      if (value === '' || value === null || value === undefined) {
        return false;
      }
      const normalized = String(value).replace(',', '.');
      const numValue = parseFloat(normalized);
      return isNaN(numValue) || numValue < 0;
    },
  }));

  useEffect(() => {
    setValue(formatNumericForDisplay(props.value));
  }, [props.value]);

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }

    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
        (e.keyCode < 96 || e.keyCode > 105) &&
        e.keyCode !== 190 && e.keyCode !== 110 && // Allow dot
        e.keyCode !== 188) { // Allow comma (German decimal separator)
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    // Allow numeric input with optional comma or dot as decimal separator
    if (inputValue === '' || /^\d*[.,]?\d*$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  return (
    <input
      type="text"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '0 8px',
        fontSize: '14px',
      }}
      autoFocus
    />
  );
});

export default NumericCellEditor;
